import axiosInstance from '../api';

export type OutboundDraftStatus = 'DRAFT' | 'APPROVED' | 'SENT' | 'REJECTED';
export type OutboundDraftKind =
  | 'REPAIR_REQUEST'
  | 'COUNTER_OFFER'
  | 'OFFER_SUBMISSION'
  | 'TITLE_INTRO'
  | 'LENDER_INTRO'
  | 'CLOSING_TIME'
  | 'GENERAL';

export interface OutboundDraftAttachment {
  name: string;
  url: string;
}

export interface OutboundDraftContext {
  address: string | null;
  party: string;
  deadline: string | null;
}

/** A queued email, as an admin sees it in the review queue. */
export interface OutboundDraft {
  id: string;
  kind: OutboundDraftKind;
  status: OutboundDraftStatus;
  transactionId: string | null;
  offerId: string | null;
  listingId: string | null;
  toName: string;
  /** Null when Reva could not identify the counterparty; an admin fills it in. */
  toEmail: string | null;
  recipientClass: string;
  /** Present on counter drafts: the terms the send will record. */
  payload?: { roundData?: Record<string, unknown> } | null;
  subject: string;
  body: string;
  attachments: OutboundDraftAttachment[];
  createdBy: string;
  approvedBy: string | null;
  approvedAt: string | null;
  sentAt: string | null;
  providerMessageId: string | null;
  rejectReason: string | null;
  createdAt: string;
  updatedAt: string;
  context: OutboundDraftContext;
}

/** What a customer may read: that a draft exists and where it stands. */
export interface OutboundDraftStatusSummary {
  id: string;
  kind: OutboundDraftKind;
  status: OutboundDraftStatus;
  transactionId: string | null;
  createdAt: string;
  sentAt: string | null;
}

/** What an admin may change on a draft before it goes out. */
export interface OutboundDraftEdits {
  subject?: string;
  body?: string;
  toName?: string;
  toEmail?: string;
  /**
   * Counter drafts are edited through their terms, never their message — the
   * server regenerates the message from these and records the offer round from
   * the same values, so the two can never disagree.
   */
  terms?: Record<string, number | string | null>;
}

export interface OutboundDraftQuery {
  status?: OutboundDraftStatus;
  kind?: OutboundDraftKind;
  transactionId?: string;
}

const outboundDraftsService = {
  find: async (query?: OutboundDraftQuery): Promise<OutboundDraft[]> => {
    const response = await axiosInstance.get<OutboundDraft[]>('/outbound-drafts', { params: query });
    return response.data;
  },

  get: async (id: string): Promise<OutboundDraft> => {
    const response = await axiosInstance.get<OutboundDraft>(`/outbound-drafts/${id}`);
    return response.data;
  },

  /** Save an edit without sending. */
  save: async (id: string, edits: OutboundDraftEdits): Promise<OutboundDraft> => {
    const response = await axiosInstance.patch<OutboundDraft>(`/outbound-drafts/${id}`, edits);
    return response.data;
  },

  /**
   * Approve and send. Any edit rides along so what was on screen is what goes
   * out. Safe to retry — the server sends a given draft at most once.
   */
  send: async (id: string, edits?: OutboundDraftEdits): Promise<OutboundDraft> => {
    const response = await axiosInstance.patch<OutboundDraft>(`/outbound-drafts/${id}`, {
      action: 'SEND',
      ...edits,
    });
    return response.data;
  },

  reject: async (id: string, rejectReason: string): Promise<OutboundDraft> => {
    const response = await axiosInstance.patch<OutboundDraft>(`/outbound-drafts/${id}`, {
      action: 'REJECT',
      rejectReason,
    });
    return response.data;
  },

  /** Drafts still waiting on a decision — what the nav badge counts. */
  getPending: async (): Promise<OutboundDraft[]> => outboundDraftsService.find({ status: 'DRAFT' }),

  /** The customer-facing "Caroline is reviewing" state for one deal. */
  getStatusForTransaction: async (transactionId: string): Promise<OutboundDraftStatusSummary[]> => {
    const response = await axiosInstance.get<OutboundDraftStatusSummary[]>('/outbound-drafts', {
      params: { transactionId },
    });
    return response.data;
  },
};

export default outboundDraftsService;
